import { message as messages, notification } from "antd";
import { history } from "umi";
import { extend } from "umi-request";
import type { RequestOptionsInit, ResponseError } from "umi-request";
import { AUTH_TOKEN_KEY } from "./tokenContext";
import { addApiRequestLog, makeRequestLogId } from "./requestLogger";

const LEGACY_AUTH_TOKEN_KEY = "PMSToken";

const AUTH_HEADER_WHITE_LIST = [
  "/user/login/api",
  "/login",
  "/screen",
  "/big-screen",
  "/bigscreen",
  "/large-screen",
];

const codeMessage: Record<number, string> = {
  200: "服务器成功返回请求的数据。",
  201: "新建或修改数据成功。",
  202: "一个请求已经进入后台排队（异步任务）。",
  204: "删除数据成功。",
  400: "发出的请求有错误，服务器没有进行新建或修改数据的操作。",
  401: "用户没有权限（令牌、用户名、密码错误）。",
  403: "用户得到授权，但是访问是被禁止的。",
  404: "发出的请求针对的是不存在的记录，服务器没有进行操作。",
  406: "请求的格式不可得。",
  410: "请求的资源被永久删除，且不会再得到的。",
  422: "当创建一个对象时，发生一个验证错误。",
  500: "服务器发生错误，请检查服务器。",
  502: "网关错误。",
  503: "服务不可用，服务器暂时过载或维护。",
  504: "网关超时。",
};

type ErrorData = {
  code?: string;
  message?: string;
};

type RequestLogMeta = {
  id: string;
  start: number;
};

type RequestOptionsWithAppMeta = RequestOptionsInit & {
  skipErrorMessage?: boolean;
};

const normalizeAuthorizationToken = (value: unknown): string => {
  if (value === undefined || value === null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const token = raw.replace(/^Bearer\s+/i, "").trim();
  if (!token) return "";
  return `Bearer ${token}`;
};

const toHeaderRecord = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) return {};

  if (headers instanceof Headers) {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  if (Array.isArray(headers)) {
    const result: Record<string, string> = {};
    headers.forEach(([key, value]) => {
      result[key] = value;
    });
    return result;
  }

  return { ...(headers as Record<string, string>) };
};

const isAuthHeaderWhiteList = (url: string): boolean => {
  return AUTH_HEADER_WHITE_LIST.some((item) => url.includes(item));
};

const getStoredToken = (): string => {
  const token = normalizeAuthorizationToken(localStorage.getItem(AUTH_TOKEN_KEY));
  if (token) return token;

  const legacyToken = normalizeAuthorizationToken(localStorage.getItem(LEGACY_AUTH_TOKEN_KEY));
  if (legacyToken) {
    localStorage.setItem(AUTH_TOKEN_KEY, legacyToken);
    localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
    return legacyToken;
  }

  return "";
};

const getRequestMethod = (options?: RequestOptionsInit) =>
  String(options?.method || "GET").toUpperCase();

const readResponseBody = async (response: Response) => {
  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return `[${contentType || "non-json response"}]`;
  }
  const cloned = response.clone();
  try {
    return await cloned.json();
  } catch (_error) {
    try {
      return await response.clone().text();
    } catch (_textError) {
      return undefined;
    }
  }
};

const errorHandler = (error: ResponseError<ErrorData>) => {
  const { response, data } = error;
  const message = data?.message;
  const requestOptions = error.request?.options as RequestOptionsWithAppMeta | undefined;
  const meta = requestOptions?.__acceptanceLogMeta as RequestLogMeta | undefined;
  const skipErrorMessage = Boolean(requestOptions?.skipErrorMessage);

  addApiRequestLog({
    id: meta?.id || makeRequestLogId(),
    time: new Date().toLocaleTimeString(),
    method: getRequestMethod(error.request?.options),
    url: error.request?.url || response?.url || "-",
    status: response?.status,
    ok: false,
    duration: meta ? Date.now() - meta.start : undefined,
    request: {
      params: error.request?.options?.params,
      data: error.request?.options?.data,
    },
    response: data,
    error: error.message,
  });

  if (response && response.status === 401) {
    localStorage.clear();
    history.replace("/user/login");
  }

  if (response?.status && !skipErrorMessage) {
    const errorText = codeMessage[response.status] || response.statusText;
    if (/^4\d{2}$/.test(String(response.status))) {
      messages.error(message || errorText);
    } else if (/^5\d{2}$/.test(String(response.status))) {
      notification.error({
        message: `错误码 ${response.status}`,
        description: data?.message || errorText,
      });
    }
  } else if (!response && !skipErrorMessage) {
    notification.error({
      message: "网络异常",
      description: "您的网络发生异常，无法连接服务器",
    });
  }

  return response;
};

const request = extend({
  errorHandler,
  prefix: API_URL_PRI,
  credentials: "include",
  timeout: 1000 * 60 * 5,
});

request.interceptors.response.use(async (response, options) => {
  const authHeader = response.headers.get("authorization");
  const token = normalizeAuthorizationToken(authHeader);
  if (token) {
    localStorage.setItem(AUTH_TOKEN_KEY, token);
  }
  const meta = options?.__acceptanceLogMeta as RequestLogMeta | undefined;
  addApiRequestLog({
    id: meta?.id || makeRequestLogId(),
    time: new Date().toLocaleTimeString(),
    method: getRequestMethod(options),
    url: response.url || options?.url || "-",
    status: response.status,
    ok: response.ok,
    duration: meta ? Date.now() - meta.start : undefined,
    request: {
      params: options?.params,
      data: options?.data,
    },
    response: await readResponseBody(response),
  });
  return response;
});

request.interceptors.request.use((url, options) => {
  const language = localStorage.getItem("umi_locale") || "zh-CN";
  const token = getStoredToken();
  const optionHeaders = toHeaderRecord(options?.headers as RequestOptionsInit["headers"]);

  const headers: Record<string, string> = {
    ...optionHeaders,
    "Accept-Language": language,
  };

  if (token && !isAuthHeaderWhiteList(url)) {
    headers.Authorization = token;
  }

  return {
    url,
    options: {
      ...options,
      headers,
      __acceptanceLogMeta: {
        id: makeRequestLogId(),
        start: Date.now(),
      },
    },
  };
});

export default request;
